import { useState, useEffect, useRef, useMemo } from "react";
import tw from "tailwind-styled-components";
import { useMutation } from "react-query";
import axios from "axios";
import Config from "../../Config";
import Loading from "../../Components/Loading";
import {
    BoxTitle,
    BoxContainer,
    BoxHead,
    Underline,
    FilterTableWrapper,
    FilterWrapper,
} from "../../Components/Styles/PageStyles";
import { useSelector } from "react-redux";
import { SummarySkeletonLoading } from "../../Components/SkeletonLoading";
import NotFoundModel from "../../Components/NotFoundModel";
import PropertiesFilterDropdown from "../../Components/PropertiesFilterDropdown";
import SideModal from "../../Components/SideModal";
import SearchFilterBox from "../../Components/SearchFilterBox";
import { format } from "date-fns";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
    FaArrowLeft,
    FaArrowRight,
    FaRegCalendarAlt,
} from "react-icons/fa";
import { MdKeyboardArrowDown } from "react-icons/md";
import Table from "../../Components/Inspection/Table";
import ViewDetails from "../../Components/Inspection/ViewDetails";
import SummaryCard from "../../Components/Inspection/SummaryCard";
import { toast } from "react-toastify";
import AddHousekeepingRecord from "../../Components/Inspection/AddHousekeepingRecord";
import CommonAreaTable from "../../Components/Inspection/CommonAreaTable";
import CommonAreaViewDetails from "../../Components/Inspection/CommonAreaViewDetails";
import { createPortal } from "react-dom";

const Housekeeping = () => {
    const searchOptions = [{ label: "Room", value: 1 }];
    const user = useSelector((state) => state.UserReducer.user);
    const property = useSelector(
        (state) => state.SelectPropertyReducer.selectProperty,
    );
    const properties = useSelector((state) => state.PropertyReducer.property);

    // ---------- Remove property with id === 0 ----------
    const filteredProperties = useMemo(
        () => (properties || []).filter((p) => p?.id !== 0),
        [properties],
    );

    // ---------- Tabs ----------
    // activeTab: "1" = Room, "2" = Common Area
    const [activeTab, setActiveTab] = useState("1");

    const [isFocused, setIsFocused] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const [isViewDetails, setIsViewDetails] = useState(false);
    const [isCommonAreaViewDetails, setIsCommonAreaViewDetails] = useState(false);
    const [isAddRecord, setIsAddRecord] = useState(false);
    const [filterType, setFilterType] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    const [apiData, setApiData] = useState([]);
    const [summaryData, setSummaryData] = useState({
        totalRooms: 0,
        cleanedRooms: 0,
        uncleanedRooms: 0,
    });
    const [taskList, setTaskList] = useState([]);
    const [commonAreaList, setCommonAreaList] = useState([]);

    // ---------- Summary filter (client-side only) ----------
    const [summaryFilter, setSummaryFilter] = useState("ALL");

    // ---------- Default selected property: skip id === 0 ----------
    const initialProperty =
        property?.id && property.id !== 0
            ? property
            : filteredProperties[0] ?? null;

    const [selectedProperty, setSelectedProperty] = useState(initialProperty);

    // ---------- Date (custom calendar) ----------
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const calendarRef = useRef(null);
    const calendarPopupRef = useRef(null);

    const [calendarPosition, setCalendarPosition] = useState({
        top: 0,
        left: 0,
    });

    // ---------- Build query (includes type from activeTab) ----------
    const query = [
        `taskDate=${format(selectedDate, "yyyy-MM-dd")}`,
        ...(selectedProperty?.id !== 0
            ? [`propFilters=${selectedProperty?.id ?? ""}`]
            : []),
        `s=${searchQuery ?? ""}`,
        `t=${filterType ?? ""}`,
        `type=${activeTab}`, // 1 = Room, 2 = Common Area
    ]
        .filter(Boolean)
        .join("&");

    // ---------- Fetch staff-task/list ----------
    const fetchFunction = async () => {
        const response = await axios.get(
            `${Config.apiUrl}/staff-task/list?${query}`,
            {
                headers: {
                    authorization: "Bearer" + " " + user.token,
                    "Content-Type":
                        "application/x-www-form-urlencoded; charset=UTF-8",
                },
            },
        );
        return response.data;
    };

    const getSuccess = (res) => {
        if (res.data === false) {
            setApiData([]);
        } else {
            setApiData(res?.data ?? []);
            setTaskList(res?.taskList ?? []);
            setCommonAreaList(res?.commonAreaTaskList ?? [])
            setSummaryData(
                res?.summary ?? {
                    totalRooms: 0,
                    cleanedRooms: 0,
                    uncleanedRooms: 0,
                },
            );
            setIsFocused(true);
        }
    };

    const getError = () => { };

    const {
        isLoading,
        mutate: getListMutate,
        error,
        isRefetching,
    } = useMutation(fetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });

    // ---------- Refetch when filters / search / date / property / tab change ----------
    useEffect(() => {
        const handler = setTimeout(() => {
            getListMutate();
        }, 200);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterType, searchQuery, selectedDate, selectedProperty, activeTab]);

    // ---------- Sync default selected property (ignoring id === 0) ----------
    useEffect(() => {
        if (selectedProperty) return;
        if (property && property.id !== 0) {
            setSelectedProperty(property);
            return;
        }
        if (filteredProperties.length > 0) {
            setSelectedProperty(filteredProperties[0]);
        }
    }, [property, filteredProperties, selectedProperty]);

    const handlePropertySelect = (property) => {
        sessionStorage.removeItem("booking_active_page");
        setSelectedProperty(property);
    };

    // ---------- Tab change handler ----------
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        // reset local filters when switching tabs
        setSummaryFilter("ALL");
        setSelectedData(null);
        setIsViewDetails(false);
        setIsCommonAreaViewDetails(false);
        sessionStorage.removeItem("housekeeping_active_page");
    };

    // ---------- Custom calendar handlers ----------
    const handleDateChange = (date) => {
        setSelectedDate(date);
        setIsCalendarOpen(false);
    };

    const toggleCalendar = () => {
        if (!isCalendarOpen && calendarRef.current) {
            const rect = calendarRef.current.getBoundingClientRect();

            setCalendarPosition({
                top: rect.bottom + 8,
                left: rect.left,
            });
        }

        setIsCalendarOpen((prev) => !prev);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            const clickedTrigger =
                calendarRef.current?.contains(event.target);

            const clickedCalendar =
                calendarPopupRef.current?.contains(event.target);

            if (!clickedTrigger && !clickedCalendar) {
                setIsCalendarOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    //------- Add Cleaning Record -------
    const AddCleaningFunction = async (values) => {
        return await axios.post(`${Config.apiUrl}/staff-task/record`, values, {
            headers: {
                authorization: "Bearer" + " " + user.token,
                "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            },
        });
    };
    const onAddCleaningSuccess = (data) => {
        if (data?.data?.isSuccess === true) {
            toast.success(data?.data?.msg || "Success");
            setIsAddRecord(false);
            getListMutate();
        } else {
            toast.error(data?.data?.msg || "An Error Occured");
        }
    };

    const onAddCleaningError = (data) => {
        toast.error(data?.response?.data?.msg || "An Error Occured");
    };

    const { isLoading: addCleaningLoading, mutate: addCleaningMutate } =
        useMutation(AddCleaningFunction, {
            onSuccess: onAddCleaningSuccess,
            onError: onAddCleaningError,
        });
    const handleClose = () => {
        setIsAddRecord(false);
    };
    const CustomCalendar = () => (
        <div className="overflow-hidden bg-white p-2">
            <div className="">
                <Calendar
                    onChange={handleDateChange}
                    value={selectedDate}
                    className="premium-calendar"
                    maxDate={new Date()}
                    prevLabel={
                        <button className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white transition-all duration-200 hover:border-primary-color hover:bg-blue-50 hover:text-primary-color">
                            <FaArrowLeft size={14} />
                        </button>
                    }
                    nextLabel={
                        <button className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white transition-all duration-200 hover:border-primary-color hover:bg-blue-50 hover:text-primary-color">
                            <FaArrowRight size={14} />
                        </button>
                    }
                    prev2Label={null}
                    next2Label={null}
                    tileClassName={({ date }) => {
                        const isToday =
                            format(date, "yyyy-MM-dd") ===
                            format(new Date(), "yyyy-MM-dd");
                        const isSelected =
                            format(date, "yyyy-MM-dd") ===
                            format(selectedDate, "yyyy-MM-dd");
                        if (isSelected) return "calendar-selected";
                        if (isToday) return "calendar-today";
                        return "calendar-day";
                    }}
                />
            </div>
        </div>
    );

    const handleFilterChange = (values) => {
        sessionStorage.removeItem("booking_active_page");
        setFilterType(values.filterType);
        setSearchQuery(values.searchQuery);
    };

    const handleClearFilters = () => {
        setFilterType("");
        setSearchQuery("");
        setSummaryFilter("ALL");
    };

    // ---------- Summary card click → local filter only ----------
    const handleSummaryClick = (filterKey) => {
        setSummaryFilter(filterKey);
        sessionStorage.removeItem("housekeeping_active_page");
    };

    // ---------- Client-side summary filter ----------
    const visibleData = useMemo(() => {
        if (!apiData?.length) return [];
        if (summaryFilter === "C") {
            return apiData.filter((r) => r.tasks?.length > 0);
        }
        if (summaryFilter === "P") {
            return apiData.filter((r) => !r.tasks?.length);
        }
        return apiData;
    }, [apiData, summaryFilter]);

    return (
        <BoxContainer>
            <BoxHead>
                <BoxTitle className="!mb-0 whitespace-nowrap">
                    Housekeeping
                    <Underline />
                </BoxTitle>
                <AddBtn
                    type="button"
                    onClick={() => setIsAddRecord(true)}
                    className="h-10"
                >
                    Add Record
                </AddBtn>
            </BoxHead>

            {/* ---------- Tabs: Room / Common Area ---------- */}
            <FloorTabs>
                <FloorTab
                    key="room"
                    onClick={() => handleTabChange("1")}
                    $active={activeTab === "1"}
                >
                    Rooms
                </FloorTab>
                <FloorTab
                    key="commonArea"
                    onClick={() => handleTabChange("2")}
                    $active={activeTab === "2"}
                >
                    Common Area
                </FloorTab>
            </FloorTabs>

            <SideModal
                isOpen={isViewDetails}
                onClose={() => setIsViewDetails(false)}
            >
                <ViewDetails
                    setIsViewDetails={setIsViewDetails}
                    selectedData={selectedData}
                    taskList={taskList}
                />
            </SideModal>

            <SideModal
                isOpen={isCommonAreaViewDetails}
                onClose={() => setIsCommonAreaViewDetails(false)}
            >
                <CommonAreaViewDetails
                    setIsViewDetails={setIsCommonAreaViewDetails}
                    selectedData={selectedData}
                    commonAreaList={commonAreaList}
                />
            </SideModal>

            <SideModal
                isOpen={isAddRecord}
                onClose={() => setIsAddRecord(false)}
            >
                <AddHousekeepingRecord
                    setIsAddRecord={setIsAddRecord}
                    taskList={taskList}
                    commonAreaList={commonAreaList}
                    handleClose={handleClose}
                    addCleaningLoading={addCleaningLoading}
                    addCleaningMutate={addCleaningMutate}
                />
            </SideModal>

            {(isLoading || isRefetching) && (activeTab === "1") && (
                <SummarySkeletonLoading count={3} />
            )}

            {!error && !isLoading && isFocused && summaryData && (activeTab === "1") && (
                <SummaryCard
                    summaryData={summaryData}
                    selectedKey={summaryFilter}
                    onClick={handleSummaryClick}
                />
            )}

            <FilterTableWrapper>
                <FilterWrapper>
                    <div className="flex md:space-x-4 md:flex-row flex-col md:gap-0 gap-4">
                        {activeTab === "1" &&
                            <SearchFilterBox
                                options={searchOptions}
                                handleFilterChange={handleFilterChange}
                                handleClearFilters={handleClearFilters}
                                className="flex-1 min-w-0"
                            />}

                        {/* ---------- Custom Calendar Dropdown ---------- */}
                        <div className="relative" ref={calendarRef}>
                            <div
                                className="flex items-center gap-2 px-3 h-10 bg-white border rounded-md cursor-pointer transition-all duration-200 min-w-[200px] "
                                onClick={toggleCalendar}
                            >
                                <FaRegCalendarAlt className="w-4 h-4 text-gray-500" />
                                <span className="text-sm text-gray-800 flex-1">
                                    {format(selectedDate, "MMM d, yyyy")}
                                </span>
                                <MdKeyboardArrowDown
                                    className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isCalendarOpen ? "rotate-180" : ""
                                        }`}
                                />
                            </div>

                            {isCalendarOpen &&
                                createPortal(
                                    <div
                                        ref={calendarPopupRef}
                                        className="fixed z-[99999] bg-white rounded-lg shadow-xl border p-3"
                                        style={{
                                            top: `${calendarPosition.top}px`,
                                            left: `${calendarPosition.left}px`,
                                            width: "380px",
                                        }}
                                    >
                                        <CustomCalendar />
                                    </div>,
                                    document.body
                                )}
                        </div>
                    </div>

                    <div className="flex gap-2 justify-end items-center ml-auto">
                        <PropertiesFilterDropdown
                            properties={properties}
                            selectedProperty={selectedProperty}
                            onPropertySelect={handlePropertySelect}
                            placeholder="Select Property"
                            className="min-w-[200px] w-full md:w-[220px]"
                        />
                    </div>
                </FilterWrapper>

                {!error && !isLoading && apiData && (
                    <TableWrapper>
                        {activeTab === "1" ?
                            <Table
                                apiData={error ? [] : visibleData}
                                taskList={taskList}
                                commonAreaList={commonAreaList}
                                setSelectedData={setSelectedData}
                                selectedData={selectedData}
                                setIsViewDetails={setIsViewDetails}
                            />
                            :
                            <CommonAreaTable
                                apiData={error ? [] : visibleData}
                                commonAreaList={commonAreaList}
                                setSelectedData={setSelectedData}
                                selectedData={selectedData}
                                setIsViewDetails={setIsCommonAreaViewDetails}
                            />}
                        {(error || visibleData?.length === 0) &&
                            !isLoading && <NotFoundModel />}
                    </TableWrapper>
                )}
            </FilterTableWrapper>

            {(isLoading || isRefetching) && <Loading />}

            {error && !isLoading && <NotFoundModel />}
        </BoxContainer>
    );
};

const TableWrapper = tw.h1`mt-4 bg-white rounded-md `;
const AddBtn = tw.button`whitespace-nowrap flex space-x-1.5 justify-center items-center text-white h-full rounded-md px-6 cursor-pointer bg-gradient-to-r from-primary-color to-[#00A0FF] hover:brightness-110 py-2.5 text-sm`;

// ---------- Tabs styles (same pattern as StaffCommission) ----------
const FloorTabs = tw.div`flex overflow-x-auto bg-white rounded-md my-4 border-b`;
const FloorTab = tw.button`
  ${(p) =>
        p.$active
            ? " text-primary-color border-b-2 border-b-primary-color bg-cyan-50 "
            : " text-gray-700 "} 
   px-5 py-3  cursor-pointer font-medium transition-all duration-200 whitespace-nowrap text-sm
`;

export default Housekeeping;